import type { BlendStage } from '@beerwright/commands';
import { BLEND_STAGES } from '@beerwright/commands';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BlendingList } from '@/components/blending/blending-list';
import { BLEND_SORTS, type BlendSort } from '@/lib/blend-search';
import { useReturnTicket } from '@/lib/return-ticket';
import { parseSearchEnum, parseSearchList, parseSearchString } from '@/lib/search-params';

/**
 * What `/blending` reads out of its own URL.
 *
 * The same vocabulary as `/recipes` and `/batches` (PLAN-LIST-STATE D-LS10):
 * `?q=` means the search box everywhere in the app and `?stage=` narrows the
 * log the way `?status=` narrows the brew log, so a blender who has learned
 * one address bar has learned all three.
 */
export interface BlendingSearch {
  /** The search box. */
  readonly q: string | undefined;
  /** Active stage chips, from a repeated `?stage=` key. */
  readonly stage: readonly BlendStage[] | undefined;
  /** The sort control, or `undefined` for the default (`updated`). */
  readonly sort: BlendSort | undefined;
}

/**
 * The repeated `?stage=` key, narrowed to stages a blend can actually be in.
 *
 * `parseSearchList` gets the shape right — one occurrence or several, trimmed
 * and de-duplicated — but it deals in strings, and this filter is compared
 * against a `BlendStage` union. A hand-typed or stale value is dropped here
 * rather than carried through as a filter nothing can ever match: a URL naming
 * only nonsense is a URL with no stage filter, not one asking for an empty log
 * (D-LS10). The brew log's own `parseStatuses` is this function, one surface
 * over.
 */
function parseStages(value: unknown): readonly BlendStage[] | undefined {
  const listed = parseSearchList(value);
  if (listed === undefined) return undefined;
  const known = listed.filter((entry): entry is BlendStage =>
    (BLEND_STAGES as readonly string[]).includes(entry),
  );
  return known.length === 0 ? undefined : known;
}

export const Route = createFileRoute('/blending/')({
  validateSearch: (
    search: {
      readonly q?: unknown;
      readonly stage?: unknown;
      readonly sort?: unknown;
    } & SearchSchemaInput,
  ): BlendingSearch => ({
    q: parseSearchString(search.q),
    stage: parseStages(search.stage),
    sort: parseSearchEnum(search.sort, BLEND_SORTS),
  }),
  component: BlendingIndexPage,
});

function BlendingIndexPage() {
  const { q, stage, sort } = Route.useSearch();
  // Scoped `from`, not the bare hook: an unscoped `useNavigate` types `search`
  // against every route's schema at once, which cannot know this route's own
  // `q`/`stage`/`sort` shape and rejects the updaters below.
  const navigate = useNavigate({ from: Route.fullPath });
  // The view is in the URL; this is what lets the blend page come back to it.
  // Opening a blend stamps the entry it pushes with this list's own position,
  // so "Blending" returns to the search the blender left rather than pushing a
  // bare `/blending` over the top of it (PLAN-LIST-STATE D-LS7).
  const returnTicket = useReturnTicket();
  return (
    <BlendingList
      // The search *is* the view: `BlendingList` reads these on every render
      // rather than seeding from them, so a Back that only changes the search
      // params — which does not remount this route — moves the list with it.
      query={q}
      stageFilter={stage}
      sort={sort}
      // `replace: true`: a keystroke narrows the same search, it does not
      // start a new one, so Back should undo the whole search box rather than
      // walk back through "g", "gu", "gue" one letter at a time.
      onQueryChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, q: next === '' ? undefined : next }),
          replace: true,
        });
      }}
      // Choosing a stage is a decision, not a keystroke: it pushes, so Back
      // undoes one chip at a time instead of jumping past the search that led
      // to it.
      onStageFilterChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, stage: next.length === 0 ? undefined : next }),
        });
      }}
      // `updated` is the log's default and never reaches the URL (D-LS2): one
      // view spelled two ways is a view Back can land on twice.
      onSortChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, sort: next === 'updated' ? undefined : next }),
        });
      }}
      // Clearing is a decision, not a keystroke: it pushes, so Back puts the
      // search the blender had just cleared back on the screen. The box and
      // the chips go in one navigation, because two would leave Back on a
      // half-cleared log nobody was ever shown.
      onClearFilters={() => {
        void navigate({ search: (prev) => ({ ...prev, q: undefined, stage: undefined }) });
      }}
      onOpen={(blendId) => {
        void navigate({ to: '/blending/$blendId', params: { blendId }, state: returnTicket });
      }}
    />
  );
}
